/**
 * ConsentHistory Component
 * Displays user's consent signature history with dates and versions
 *
 * MIGRATED: Custom CSS → Tailwind CSS (Mobile-First & Desktop-Ready)
 */

import React, { useState, useEffect } from 'react';
import { FileText, CheckCircle, Clock, AlertCircle } from 'lucide-react';
import { config } from '@/src/config';
import { cn } from '@/lib/utils';

import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import { authFetch } from '@/lib/authFetch';

interface ConsentSignature {
  id: number;
  document_type: string;
  document_version: string;
  document_title: string;
  signature_text: string;
  signed_at: string;
  consent_given: boolean;
  revoked: boolean;
}

interface ConsentStatus {
  terms_current_version: string | null;
  terms_signed_version: string | null;
  terms_signed_at: string | null;
  terms_needs_update: boolean;
  // Healthcare-only (omitted from Triveny /api/consent/history payload)
  hipaa_current_version?: string | null;
  hipaa_signed_version?: string | null;
  hipaa_signed_at?: string | null;
  hipaa_needs_update?: boolean;
  // Triveny-only — populated by PlatformConsentStatus from the backend
  // (app/routers/consent_management.py). Healthcare payloads omit these.
  privacy_current_version?: string | null;
  privacy_signed_version?: string | null;
  privacy_signed_at?: string | null;
  privacy_needs_update?: boolean;
  ai_current_version?: string | null;
  ai_signed_version?: string | null;
  ai_signed_at?: string | null;
  ai_needs_update?: boolean;
  all_consents_current: boolean;
}

interface ConsentHistoryData {
  status: ConsentStatus;
  signatures: ConsentSignature[];
}

export const ConsentHistory: React.FC = () => {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  const [history, setHistory] = useState<ConsentHistoryData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchConsentHistory();
  }, []);

  const fetchConsentHistory = async () => {
    try {
      setLoading(true);

      const response = await authFetch(`${config.weaveinApi.baseUrl}/api/consent/history`);

      if (!response.ok) {
        throw new Error('Failed to load consent history');
      }

      const data = await response.json();
      setHistory(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load consent history');
    } finally {
      setLoading(false);
    }
  };

  const getDocumentTypeLabel = (type: string): string => {
    switch (type) {
      case 'terms_of_use':
        return 'Terms of Use';
      case 'hipaa_authorization':
        return 'HIPAA Authorization';
      case 'privacy_policy':
        return 'Privacy Policy';
      case 'ai_disclaimers':
        return 'AI Disclaimers';
      default:
        return type;
    }
  };

  const formatDate = (dateString: string): string => {
    return fmt.dateTime(dateString);
  };

  if (loading) {
    return (
      <div className="p-4 md:p-6 lg:p-8 max-w-4xl mx-auto">
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">{t('settings.loading_consent_history', 'Loading consent history...')}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 md:p-6 lg:p-8 max-w-4xl mx-auto">
        <div className="flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-red-600">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      </div>
    );
  }

  if (!history) {
    return null;
  }

  const { status, signatures } = history;

  // Group signatures by document type
  const termsSignatures = signatures.filter(s => s.document_type === 'terms_of_use');
  const hipaaSignatures = signatures.filter(s => s.document_type === 'hipaa_authorization');
  const privacySignatures = signatures.filter(s => s.document_type === 'privacy_policy');
  const aiSignatures = signatures.filter(s => s.document_type === 'ai_disclaimers');

  return (
    <div className={`p-4 md:p-6 lg:p-8 max-w-4xl mx-auto space-y-6${config.isTriveny ? ' triveny-platform' : ''}`}>
      {/* Header */}
      <div className="flex items-center gap-3">
        <FileText className="w-8 h-8 text-blue-600 flex-shrink-0" />
        <div>
          <h2 className="text-xl md:text-2xl font-semibold text-gray-900 dark:text-white">{t('settings.consent_signature_history', 'Consent & Signature History')}</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{t('settings.view_your_consent_documents_and', 'View your consent documents and signature history')}</p>
        </div>
      </div>

      {/* Overall Status Banner */}
      <div className={cn(
        "flex items-center gap-3 px-4 py-3 rounded-lg",
        status.all_consents_current
          ? "bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700 text-green-700 dark:text-green-300"
          : "bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-700 text-yellow-700 dark:text-yellow-300"
      )}>
        {status.all_consents_current ? (
          <>
            <CheckCircle className="w-5 h-5 flex-shrink-0" />
            <span className="font-medium">{t('settings.all_consents_are_up_to', 'All consents are up to date')}</span>
          </>
        ) : (
          <>
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <span className="font-medium">{t('settings.action_required_one_or_more', 'Action required: One or more consents need to be updated')}</span>
          </>
        )}
      </div>

      {/* Terms of Use Section */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 md:p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <FileText className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0" />
            <h3 className="font-semibold text-gray-900 dark:text-white">{t('settings.terms_of_use', 'Terms of Use')}</h3>
          </div>
          <span className={cn(
            "inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium",
            status.terms_needs_update
              ? "bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300"
              : "bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300"
          )}>
            {status.terms_needs_update ? (
              <>
                <Clock className="w-4 h-4" />
                <span>{t('settings.update_required', 'Update Required')}</span>
              </>
            ) : (
              <>
                <CheckCircle className="w-4 h-4" />
                <span>{t('settings.current', 'Current')}</span>
              </>
            )}
          </span>
        </div>

        <div className="p-4 md:p-6 space-y-4">
          {/* Current Info */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <span className="text-xs text-gray-500 dark:text-gray-400 block">{t('settings.current_version', 'Current Version')}</span>
              <span className="font-medium text-gray-900 dark:text-white">{status.terms_current_version || 'N/A'}</span>
            </div>
            <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <span className="text-xs text-gray-500 dark:text-gray-400 block">{t('settings.your_signed_version', 'Your Signed Version')}</span>
              <span className="font-medium text-gray-900 dark:text-white">{status.terms_signed_version || 'Not signed'}</span>
            </div>
            {status.terms_signed_at && (
              <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <span className="text-xs text-gray-500 dark:text-gray-400 block">{t('settings.last_signed', 'Last Signed')}</span>
                <span className="font-medium text-gray-900 dark:text-white text-sm">{formatDate(status.terms_signed_at)}</span>
              </div>
            )}
          </div>

          {/* Update Notice */}
          {status.terms_needs_update && (
            <div className="flex items-start gap-2 p-3 bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-700 rounded-lg text-yellow-700 dark:text-yellow-300">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span className="text-sm">{t('settings.a_new_version_of_the', 'A new version of the Terms of Use is available. You will be prompted to review and sign it on your next login.')}</span>
            </div>
          )}

          {/* Signature History */}
          {termsSignatures.length > 0 && (
            <div className="space-y-3">
              <h4 className="font-medium text-gray-900 dark:text-white text-sm">{t('settings.signature_history', 'Signature History')}</h4>
              <div className="space-y-2">
                {termsSignatures.map((sig) => (
                  <div
                    key={sig.id}
                    className={cn(
                      "p-3 border rounded-lg",
                      sig.revoked
                        ? "bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 opacity-60"
                        : "bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700"
                    )}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
                      <span className="font-medium text-gray-900 dark:text-white text-sm">Version {sig.document_version}</span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">{formatDate(sig.signed_at)}</span>
                    </div>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                      <div className="flex-1">
                        <span className="text-xs text-gray-500 dark:text-gray-400">{t('settings.signature', 'Signature:')} </span>
                        <span className="text-sm text-gray-700 dark:text-gray-300 italic">{sig.signature_text}</span>
                      </div>
                      {sig.revoked && (
                        <span className="px-2 py-0.5 bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 text-xs font-medium rounded">{t('settings.revoked', 'Revoked')}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* HIPAA Authorization Section — hidden on Triveny (no PHI handling, so no
          HIPAA consent model exists). Triveny tenants get Privacy + AI
          Disclaimers via TrivenyHome's footer links / TrivenyConsentGate
          instead; surfacing HIPAA here would falsely imply a covered-entity
          relationship that doesn't exist for B2B marketing/sales tenants. */}
      {!config.isTriveny && (
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 md:p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <FileText className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0" />
            <h3 className="font-semibold text-gray-900 dark:text-white">{t('settings.hipaa_authorization', 'HIPAA Authorization')}</h3>
          </div>
          <span className={cn(
            "inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium",
            status.hipaa_needs_update
              ? "bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300"
              : "bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300"
          )}>
            {status.hipaa_needs_update ? (
              <>
                <Clock className="w-4 h-4" />
                <span>{t('settings.update_required_1', 'Update Required')}</span>
              </>
            ) : (
              <>
                <CheckCircle className="w-4 h-4" />
                <span>{t('settings.current_1', 'Current')}</span>
              </>
            )}
          </span>
        </div>

        <div className="p-4 md:p-6 space-y-4">
          {/* Current Info */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <span className="text-xs text-gray-500 dark:text-gray-400 block">{t('settings.current_version_1', 'Current Version')}</span>
              <span className="font-medium text-gray-900 dark:text-white">{status.hipaa_current_version || 'N/A'}</span>
            </div>
            <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <span className="text-xs text-gray-500 dark:text-gray-400 block">{t('settings.your_signed_version_1', 'Your Signed Version')}</span>
              <span className="font-medium text-gray-900 dark:text-white">{status.hipaa_signed_version || 'Not signed'}</span>
            </div>
            {status.hipaa_signed_at && (
              <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <span className="text-xs text-gray-500 dark:text-gray-400 block">{t('settings.last_signed_1', 'Last Signed')}</span>
                <span className="font-medium text-gray-900 dark:text-white text-sm">{formatDate(status.hipaa_signed_at)}</span>
              </div>
            )}
          </div>

          {/* Update Notice */}
          {status.hipaa_needs_update && (
            <div className="flex items-start gap-2 p-3 bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-700 rounded-lg text-yellow-700 dark:text-yellow-300">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span className="text-sm">{t('settings.a_new_version_of_the_1', 'A new version of the HIPAA Authorization is available. You will be prompted to review and sign it on your next login.')}</span>
            </div>
          )}

          {/* Signature History */}
          {hipaaSignatures.length > 0 && (
            <div className="space-y-3">
              <h4 className="font-medium text-gray-900 dark:text-white text-sm">{t('settings.signature_history_1', 'Signature History')}</h4>
              <div className="space-y-2">
                {hipaaSignatures.map((sig) => (
                  <div
                    key={sig.id}
                    className={cn(
                      "p-3 border rounded-lg",
                      sig.revoked
                        ? "bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 opacity-60"
                        : "bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700"
                    )}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
                      <span className="font-medium text-gray-900 dark:text-white text-sm">Version {sig.document_version}</span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">{formatDate(sig.signed_at)}</span>
                    </div>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                      <div className="flex-1">
                        <span className="text-xs text-gray-500 dark:text-gray-400">{t('settings.signature_1', 'Signature:')} </span>
                        <span className="text-sm text-gray-700 dark:text-gray-300 italic">{sig.signature_text}</span>
                      </div>
                      {sig.revoked && (
                        <span className="px-2 py-0.5 bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 text-xs font-medium rounded">{t('settings.revoked_1', 'Revoked')}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      )}

      {/* Privacy Policy Section — Triveny only. Healthcare consent uses HIPAA
          Authorization (above); Triveny has no PHI, so Privacy Policy +
          AI Disclaimers replace HIPAA as the two non-Terms consent docs. */}
      {config.isTriveny && (
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 md:p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <FileText className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0" />
            <h3 className="font-semibold text-gray-900 dark:text-white">{t('settings.privacy_policy', 'Privacy Policy')}</h3>
          </div>
          <span className={cn(
            "inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium",
            status.privacy_needs_update
              ? "bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300"
              : "bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300"
          )}>
            {status.privacy_needs_update ? (
              <>
                <Clock className="w-4 h-4" />
                <span>{t('settings.update_required', 'Update Required')}</span>
              </>
            ) : (
              <>
                <CheckCircle className="w-4 h-4" />
                <span>{t('settings.current', 'Current')}</span>
              </>
            )}
          </span>
        </div>

        <div className="p-4 md:p-6 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <span className="text-xs text-gray-500 dark:text-gray-400 block">{t('settings.current_version', 'Current Version')}</span>
              <span className="font-medium text-gray-900 dark:text-white">{status.privacy_current_version || 'N/A'}</span>
            </div>
            <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <span className="text-xs text-gray-500 dark:text-gray-400 block">{t('settings.your_signed_version', 'Your Signed Version')}</span>
              <span className="font-medium text-gray-900 dark:text-white">{status.privacy_signed_version || 'Not signed'}</span>
            </div>
            {status.privacy_signed_at && (
              <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <span className="text-xs text-gray-500 dark:text-gray-400 block">{t('settings.last_signed', 'Last Signed')}</span>
                <span className="font-medium text-gray-900 dark:text-white text-sm">{formatDate(status.privacy_signed_at)}</span>
              </div>
            )}
          </div>

          {status.privacy_needs_update && (
            <div className="flex items-start gap-2 p-3 bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-700 rounded-lg text-yellow-700 dark:text-yellow-300">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span className="text-sm">{t('settings.privacy_needs_update', 'A new version of the Privacy Policy is available. You will be prompted to review and sign it on your next login.')}</span>
            </div>
          )}

          {privacySignatures.length > 0 && (
            <div className="space-y-3">
              <h4 className="font-medium text-gray-900 dark:text-white text-sm">{t('settings.signature_history', 'Signature History')}</h4>
              <div className="space-y-2">
                {privacySignatures.map((sig) => (
                  <div
                    key={sig.id}
                    className={cn(
                      "p-3 border rounded-lg",
                      sig.revoked
                        ? "bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 opacity-60"
                        : "bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700"
                    )}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
                      <span className="font-medium text-gray-900 dark:text-white text-sm">Version {sig.document_version}</span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">{formatDate(sig.signed_at)}</span>
                    </div>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                      <div className="flex-1">
                        <span className="text-xs text-gray-500 dark:text-gray-400">{t('settings.signature', 'Signature:')} </span>
                        <span className="text-sm text-gray-700 dark:text-gray-300 italic">{sig.signature_text}</span>
                      </div>
                      {sig.revoked && (
                        <span className="px-2 py-0.5 bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 text-xs font-medium rounded">{t('settings.revoked', 'Revoked')}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      )}

      {/* AI Disclaimers Section — Triveny only. The AI features in Triveny
          (Studio AI, Voice AI, Lead Builder enrichment, Intent intelligence)
          surface model outputs that require an explicit acknowledgment per
          the AI Use & Limitations document. */}
      {config.isTriveny && (
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 md:p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <FileText className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0" />
            <h3 className="font-semibold text-gray-900 dark:text-white">{t('settings.ai_disclaimers', 'AI Disclaimers')}</h3>
          </div>
          <span className={cn(
            "inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium",
            status.ai_needs_update
              ? "bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300"
              : "bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300"
          )}>
            {status.ai_needs_update ? (
              <>
                <Clock className="w-4 h-4" />
                <span>{t('settings.update_required', 'Update Required')}</span>
              </>
            ) : (
              <>
                <CheckCircle className="w-4 h-4" />
                <span>{t('settings.current', 'Current')}</span>
              </>
            )}
          </span>
        </div>

        <div className="p-4 md:p-6 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <span className="text-xs text-gray-500 dark:text-gray-400 block">{t('settings.current_version', 'Current Version')}</span>
              <span className="font-medium text-gray-900 dark:text-white">{status.ai_current_version || 'N/A'}</span>
            </div>
            <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <span className="text-xs text-gray-500 dark:text-gray-400 block">{t('settings.your_signed_version', 'Your Signed Version')}</span>
              <span className="font-medium text-gray-900 dark:text-white">{status.ai_signed_version || 'Not signed'}</span>
            </div>
            {status.ai_signed_at && (
              <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <span className="text-xs text-gray-500 dark:text-gray-400 block">{t('settings.last_signed', 'Last Signed')}</span>
                <span className="font-medium text-gray-900 dark:text-white text-sm">{formatDate(status.ai_signed_at)}</span>
              </div>
            )}
          </div>

          {status.ai_needs_update && (
            <div className="flex items-start gap-2 p-3 bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-700 rounded-lg text-yellow-700 dark:text-yellow-300">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span className="text-sm">{t('settings.ai_needs_update', 'A new version of the AI Disclaimers is available. You will be prompted to review and sign it on your next login.')}</span>
            </div>
          )}

          {aiSignatures.length > 0 && (
            <div className="space-y-3">
              <h4 className="font-medium text-gray-900 dark:text-white text-sm">{t('settings.signature_history', 'Signature History')}</h4>
              <div className="space-y-2">
                {aiSignatures.map((sig) => (
                  <div
                    key={sig.id}
                    className={cn(
                      "p-3 border rounded-lg",
                      sig.revoked
                        ? "bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 opacity-60"
                        : "bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700"
                    )}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
                      <span className="font-medium text-gray-900 dark:text-white text-sm">Version {sig.document_version}</span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">{formatDate(sig.signed_at)}</span>
                    </div>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                      <div className="flex-1">
                        <span className="text-xs text-gray-500 dark:text-gray-400">{t('settings.signature', 'Signature:')} </span>
                        <span className="text-sm text-gray-700 dark:text-gray-300 italic">{sig.signature_text}</span>
                      </div>
                      {sig.revoked && (
                        <span className="px-2 py-0.5 bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 text-xs font-medium rounded">{t('settings.revoked', 'Revoked')}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      )}

      {/* Help Text */}
      <div className="p-4 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg">
        <p className="text-sm text-gray-600 dark:text-gray-300">
          <strong>{t('settings.about_consent_versioning', 'About Consent Versioning:')}</strong> When new versions of consent documents are published,
          you will be automatically prompted to review and sign them upon your next login. Your signature
          history is maintained for compliance and audit purposes.
        </p>
      </div>
    </div>
  );
};
